import React, { useEffect, useState, lazy, createElement } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Check, Lock, Mail, Smartphone, Star, ShieldCheck, Sparkles, Clock, Heart, ArrowRight, X } from 'lucide-react';
// ---------- Checkout (Shopify Cart Permalinks) ----------
const MAIN_PRODUCT_VARIANT_ID = '44739342434390';
const UPSELL_PRODUCT_VARIANT_ID = '44802754183254';
const SHOPIFY_CART_DOMAIN = 'https://impulsodigital.vip/cart/';
const CHECKOUT_MAIN_ONLY = `${SHOPIFY_CART_DOMAIN}${MAIN_PRODUCT_VARIANT_ID}:1`;
const CHECKOUT_WITH_UPSELL = `${SHOPIFY_CART_DOMAIN}${MAIN_PRODUCT_VARIANT_ID}:1,${UPSELL_PRODUCT_VARIANT_ID}:1`;
function goToMainOnlyCheckout() {
  window.location.href = CHECKOUT_MAIN_ONLY;
}
function goToUpsellCheckout() {
  window.location.href = CHECKOUT_WITH_UPSELL;
}
function openUpsellModal() {
  window.dispatchEvent(new CustomEvent('open-upsell-modal'));
}
const HERO_IMAGE = 'https://cdn.magicpatterns.com/uploads/kHy3JbhLSKTGVzHFERTvSy/hero_psicologa_lcp_optimized.webp';
// Performance: establish early connections + preload the hero (LCP) image.
// Runs at module load, before render, so the browser starts work ASAP.
if (typeof document !== 'undefined') {
  const head = document.head;
  const addLink = (attrs: Record<string, string>) => {
    const selector = `link[data-perf="${attrs['data-perf']}"]`;
    if (document.querySelector(selector)) return;
    const link = document.createElement('link');
    Object.entries(attrs).forEach(([k, v]) => link.setAttribute(k, v));
    head.appendChild(link);
  };
  addLink({
    rel: 'preconnect',
    href: 'https://cdn.magicpatterns.com',
    crossorigin: '',
    'data-perf': 'preconnect-cdn'
  });
  addLink({
    rel: 'dns-prefetch',
    href: 'https://cdn.magicpatterns.com',
    'data-perf': 'dns-cdn'
  });
  addLink({
    rel: 'preconnect',
    href: 'https://fonts.googleapis.com',
    'data-perf': 'preconnect-gfonts-css'
  });
  addLink({
    rel: 'preconnect',
    href: 'https://fonts.gstatic.com',
    crossorigin: '',
    'data-perf': 'preconnect-gfonts-files'
  });
  addLink({
    rel: 'preload',
    as: 'image',
    href: HERO_IMAGE,
    fetchpriority: 'high',
    'data-perf': 'hero-preload'
  });
  // Checkout domain: warm DNS/TCP/SSL early. We do NOT preload any cart URL —
  // the dynamic Shopify permalink is only built when the user confirms.
  addLink({
    rel: 'preconnect',
    href: 'https://impulsodigital.vip',
    crossorigin: '',
    'data-perf': 'preconnect-checkout'
  });
  addLink({
    rel: 'dns-prefetch',
    href: '//impulsodigital.vip',
    'data-perf': 'dns-checkout'
  });
}
// Re-warm the checkout connection on intent signals (viewport / hover / touch /
// modal open). Idempotent + throttled: it re-inserts a fresh preconnect link so
// an idle (closed) connection is re-established just before the user navigates.
// It NEVER fetches a cart URL, builds a cart, or fires any event.
let lastWarm = 0;
function warmCheckout() {
  if (typeof document === 'undefined') return;
  const now = Date.now();
  if (now - lastWarm < 4000) return; // throttle: connections stay warm ~10s
  lastWarm = now;
  const prev = document.querySelector('link[data-perf="preconnect-checkout-warm"]');
  if (prev) prev.remove();
  const link = document.createElement('link');
  link.rel = 'preconnect';
  link.href = 'https://impulsodigital.vip';
  link.setAttribute('crossorigin', '');
  link.setAttribute('data-perf', 'preconnect-checkout-warm');
  document.head.appendChild(link);
}
const PRICE_OLD = '$49.990';
const PRICE_NEW = '$19.990';
// ---------- Reusable CTA ----------
const CTAButton = ({
  text = 'QUIERO EMPEZAR A ELEGIRME',
  className = '',
  size = 'lg'
}: {
  text?: string;
  className?: string;
  size?: 'lg' | 'md';
}) => <button type="button" onClick={openUpsellModal} onMouseEnter={warmCheckout} onTouchStart={warmCheckout} className={`group relative inline-flex items-center justify-center w-full max-w-lg mx-auto bg-brand-pink hover:bg-brand-pinkHover text-white font-semibold tracking-wide rounded-full text-center transition-all duration-300 shadow-[0_8px_20px_-6px_rgba(236,72,153,0.6)] hover:shadow-[0_12px_28px_-6px_rgba(236,72,153,0.7)] hover:-translate-y-0.5 ${size === 'lg' ? 'text-base md:text-lg py-4 md:py-5 px-8' : 'text-sm md:text-base py-3 md:py-3.5 px-6'} ${className}`} data-id="element-0">
    <span data-id="element-1">{text}</span>
    <ArrowRight className="w-4 h-4 md:w-5 md:h-5 ml-2 transition-transform group-hover:translate-x-1" data-id="element-2" />
  </button>;
// ---------- Compact Check Row ----------
const CheckRow = ({
  children
}: {
  children: React.ReactNode;
}) => <div className="flex items-start gap-2.5" data-id="element-3">
    <div className="flex-shrink-0 mt-0.5 w-5 h-5 rounded-full bg-green-50 border border-green-200 flex items-center justify-center" data-id="element-4">
      <Check className="w-3 h-3 text-green-600" strokeWidth={3} data-id="element-5" />
    </div>
    <p className="text-[15px] md:text-base font-medium text-gray-700 leading-relaxed" data-id="element-6">
      {children}
    </p>
  </div>;
// ---------- Bonus Item ----------
const BonusItem = ({
  img,
  title,
  value
}: {
  img: string;
  title: string;
  value: string;
}) => <div className="relative flex items-center gap-3 bg-white p-3 rounded-xl border border-gray-100 shadow-sm" data-id="element-7">
    <span className="absolute -top-2 -right-2 bg-brand-pink text-white text-[9px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wider shadow" data-id="element-8">
      Gratis
    </span>
    <img src={img} alt={title} loading="lazy" decoding="async" className="w-12 h-16 md:w-14 md:h-[72px] object-cover rounded-md shadow-md flex-shrink-0" data-id="element-9" />
    <div className="flex-1 min-w-0" data-id="element-10">
      <p className="font-semibold text-gray-900 text-sm md:text-[15px] leading-tight" data-id="element-11">
        {title}
      </p>
      <p className="text-xs text-gray-400 mt-1" data-id="element-12">
        Valor <span className="line-through" data-id="element-13">{value}</span>
      </p>
    </div>
  </div>;
// ---------- Testimonial ----------
const Testimonial = ({
  quote,
  name,
  country
}: {
  quote: string;
  name: string;
  country: string;
}) => <div className="bg-white p-4 md:p-5 rounded-2xl border border-gray-100 shadow-sm flex flex-col" data-id="element-14">
    <div className="flex gap-0.5 mb-2" data-id="element-15">
      {[...Array(5)].map((_, i) => <Star key={i} className="w-3.5 h-3.5 fill-yellow-400 text-yellow-400" data-id="element-16" />)}
    </div>
    <p className="text-gray-700 text-[14px] md:text-[15px] leading-relaxed mb-3 flex-grow" data-id="element-17">
      "{quote}"
    </p>
    <div className="flex items-center gap-2 pt-2 border-t border-gray-50" data-id="element-18">
      <div className="w-7 h-7 rounded-full bg-pink-50 flex items-center justify-center text-brand-pink font-bold text-xs" data-id="element-19">
        {name.charAt(0)}
      </div>
      <div data-id="element-20">
        <p className="text-xs font-semibold text-gray-900" data-id="element-21">{name}</p>
        <p className="text-[10px] text-gray-400" data-id="element-22">{country}</p>
      </div>
    </div>
  </div>;
// ---------- Sticky Mobile CTA ----------
const StickyCTA = () => {
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const onScroll = () => setVisible(window.scrollY > 600);
    window.addEventListener('scroll', onScroll, {
      passive: true
    });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, []);
  return <div className={`fixed bottom-0 left-0 right-0 z-40 lg:hidden bg-white/95 backdrop-blur-md border-t border-gray-200 shadow-[0_-8px_24px_-12px_rgba(0,0,0,0.15)] px-4 py-3 transition-transform duration-300 ease-out ${visible ? 'translate-y-0' : 'translate-y-full'}`} data-id="element-23">
      <div className="flex items-center justify-between gap-3 max-w-md mx-auto" data-id="element-24">
        <div className="flex flex-col leading-tight" data-id="element-25">
          <span className="text-[11px] text-gray-400 line-through" data-id="element-26">
            {PRICE_OLD}
          </span>
          <span className="text-lg font-bold text-gray-900" data-id="element-27">
            {PRICE_NEW}{' '}
            <span className="text-[11px] font-medium text-gray-500" data-id="element-28">ARS</span>
          </span>
        </div>
        <button type="button" onClick={openUpsellModal} onMouseEnter={warmCheckout} onTouchStart={warmCheckout} className="flex-1 max-w-[200px] bg-brand-pink hover:bg-brand-pinkHover text-white text-sm font-semibold py-3 px-4 rounded-full text-center transition-all shadow-[0_6px_16px_-4px_rgba(236,72,153,0.55)] active:scale-95" data-id="element-29">
          QUIERO EMPEZAR
        </button>
      </div>
    </div>;
};
// ---------- Section Wrapper (consistent compact padding) ----------
const Section = ({
  children,
  className = ''
}: {
  children: React.ReactNode;
  className?: string;
}) => <section className={`py-8 md:py-12 px-4 ${className}`} data-id="element-30">{children}</section>;
// ---------- Upsell Modal ----------
const UpsellModal = () => {
  const [open, setOpen] = useState(false);
  useEffect(() => {
    const handler = () => {
      setOpen(true);
      warmCheckout(); // user showed buy intent — warm checkout connection
    };
    window.addEventListener('open-upsell-modal', handler as EventListener);
    return () => window.removeEventListener('open-upsell-modal', handler as EventListener);
  }, []);
  useEffect(() => {
    if (open) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [open]);
  const close = () => setOpen(false);
  const benefits = ['Fortalecé tu amor propio desde adentro', 'Aprendé a poner límites sin culpa', 'Recuperá seguridad y confianza en vos misma', 'Ideal para dejar de depender de la validación externa', 'Ejercicios y reflexiones para trabajar tu autoestima', 'Recibís todo junto en formato digital'];
  return <AnimatePresence data-id="element-31">
      {open && <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} transition={{
      duration: 0.2
    }} className="fixed inset-0 z-[100] flex items-center justify-center px-4 py-6 sm:p-6" data-id="element-32">
          {/* Overlay */}
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={close} aria-hidden="true" data-id="element-33" />

          {/* Card */}
          <motion.div initial={{
        y: 30,
        opacity: 0,
        scale: 0.96
      }} animate={{
        y: 0,
        opacity: 1,
        scale: 1
      }} exit={{
        y: 20,
        opacity: 0,
        scale: 0.97
      }} transition={{
        duration: 0.25,
        ease: 'easeOut'
      }} className="relative w-full max-w-[380px] sm:max-w-md bg-gradient-to-b from-rose-50/80 via-white to-white rounded-3xl shadow-2xl border border-rose-100 overflow-hidden max-h-[92vh] overflow-y-auto" role="dialog" aria-modal="true" aria-labelledby="upsell-title" data-id="element-34">
            {/* Close */}
            <button type="button" onClick={close} aria-label="Cerrar" className="absolute top-3 right-3 z-10 w-9 h-9 rounded-full bg-white/90 hover:bg-white border border-gray-200 shadow-sm flex items-center justify-center text-gray-500 hover:text-gray-700 transition-colors" data-id="element-35">
              <X className="w-4 h-4" data-id="element-36" />
            </button>

            <div className="px-4 pt-6 pb-5 sm:px-6 sm:pt-7 sm:pb-6" data-id="element-37">
              {/* Badge */}
              <div className="flex justify-center mb-4" data-id="element-38">
                <div className="inline-flex items-center gap-1.5 bg-amber-50 border border-amber-200 text-amber-700 text-[10px] sm:text-xs font-bold uppercase tracking-[0.15em] px-3 py-1.5 rounded-full" data-id="element-39">
                  <Sparkles className="w-3 h-3" data-id="element-40" />
                  Oferta especial por única vez
                </div>
              </div>

              {/* Title */}
              <h2 id="upsell-title" className="text-lg sm:text-xl font-black text-gray-900 text-center leading-tight mb-1.5" data-id="element-41">
                Antes de finalizar tu compra…
              </h2>

              {/* Subtitle */}
              <p className="text-center text-gray-700 text-[13px] sm:text-sm leading-snug mb-4" data-id="element-42">
                Agregá{' '}
                <span className="font-bold text-brand-pink" data-id="element-43">
                  "Amor Propio y Autoestima Inquebrantable"
                </span>{' '}
                por solo <span className="font-bold" data-id="element-44">$3.990</span>
              </p>

              {/* Book cover */}
              <div className="flex justify-center mb-4" data-id="element-45">
                <img src="https://cdn.magicpatterns.com/uploads/9vNoQFDvEKJ8kjEsHXLGGy/amor_propio_upsell_optimized.webp" alt="Amor Propio y Autoestima Inquebrantable - Ebook" loading="lazy" decoding="async" className="w-20 sm:w-24 h-auto object-contain rounded-md shadow-xl" data-id="element-46" />
              </div>

              {/* Emotional copy */}
              <div className="bg-white/70 border border-rose-100 rounded-xl p-3 sm:p-3.5 mb-4" data-id="element-47">
                <p className="text-[12px] sm:text-[13px] text-gray-700 leading-relaxed" data-id="element-48">
                  Ya estás dando el primer paso para elegirte primero. Este
                  ebook es el complemento perfecto para fortalecer tu
                  autoestima, sanar la relación con vos misma y volver a
                  conectar con la persona más importante de tu vida:{' '}
                  <span className="font-handwriting text-base text-brand-pink" data-id="element-49">
                    vos.
                  </span>
                </p>
              </div>

              {/* Price block */}
              <div className="bg-white border border-gray-200 rounded-xl px-3 py-3 sm:px-4 sm:py-3.5 mb-4 text-center shadow-sm" data-id="element-50">
                <p className="text-[11px] text-gray-400 line-through font-medium mb-0.5" data-id="element-51">
                  Antes: $10.000
                </p>
                <p className="text-xl sm:text-2xl font-black text-brand-pink leading-tight" data-id="element-52">
                  Hoy: $3.990
                </p>
                <p className="text-[10.5px] sm:text-[11px] text-gray-500 mt-1.5 leading-snug" data-id="element-53">
                  Tu compra total queda en{' '}
                  <span className="font-semibold text-gray-700" data-id="element-54">$13.980</span>
                </p>
              </div>

              {/* Benefits */}
              <ul className="space-y-1.5 mb-5" data-id="element-55">
                {benefits.map((b, i) => <li key={i} className="flex items-start gap-2" data-id="element-56">
                    <span className="flex-shrink-0 mt-0.5 w-3.5 h-3.5 rounded-full bg-brand-pink/10 flex items-center justify-center" data-id="element-57">
                      <Check className="w-2 h-2 text-brand-pink" strokeWidth={3} data-id="element-58" />
                    </span>
                    <span className="text-[12px] sm:text-[13px] text-gray-700 leading-snug" data-id="element-59">
                      {b}
                    </span>
                  </li>)}
              </ul>

              {/* Primary CTA */}
              <button type="button" onClick={goToUpsellCheckout} onMouseEnter={warmCheckout} onTouchStart={warmCheckout} className="group w-full bg-brand-pink hover:bg-brand-pinkHover text-white font-bold text-[14px] sm:text-[15px] py-3.5 px-4 rounded-full transition-all duration-200 shadow-[0_8px_20px_-6px_rgba(236,72,153,0.55)] hover:-translate-y-0.5 inline-flex items-center justify-center" data-id="element-60">
                Sí, agregar por solo $3.990
                <ArrowRight className="w-3.5 h-3.5 ml-1.5 transition-transform group-hover:translate-x-1" data-id="element-61" />
              </button>

              {/* Secondary CTA */}
              <button type="button" onClick={goToMainOnlyCheckout} onMouseEnter={warmCheckout} onTouchStart={warmCheckout} className="w-full mt-2.5 text-gray-500 hover:text-gray-700 text-[12px] sm:text-[13px] font-medium underline underline-offset-4 decoration-gray-300 hover:decoration-gray-500 py-1.5 transition-colors" data-id="element-62">
                No gracias, continuar solo con mi programa
              </button>

              {/* Trust text */}
              <p className="text-center text-[10px] sm:text-[11px] text-gray-400 mt-3 leading-relaxed" data-id="element-63">
                Se agrega automáticamente a tu compra y recibís todo junto en
                formato digital.
              </p>
            </div>
          </motion.div>
        </motion.div>}
    </AnimatePresence>;
};
// ---------- MAIN ----------
export function App() {
  return <div className="min-h-screen bg-white text-gray-900 overflow-x-hidden selection:bg-brand-pink selection:text-white antialiased pb-24 lg:pb-0" data-id="element-64">
      {/* ============ HERO ============ */}
      <section className="pt-6 pb-8 md:pt-10 md:pb-12 px-4 max-w-5xl mx-auto text-center" data-id="element-65">
        <div className="inline-flex items-center gap-1.5 bg-pink-50 border border-pink-100 text-brand-pink text-[11px] md:text-xs font-semibold px-3 py-1.5 rounded-full mb-5 uppercase tracking-wider" data-id="element-66">
          <Sparkles className="w-3 h-3" data-id="element-67" />
          Programa Digital · Mi Psicóloga Me Dijo
        </div>

        <h1 className="text-[28px] leading-[1.1] md:text-5xl lg:text-[56px] font-black text-gray-900 tracking-tight mb-3 max-w-4xl mx-auto" data-id="element-68">
          110 sesiones para dejar de{' '}
          <span className="text-brand-pink" data-id="element-69">abandonarte</span>{' '}
          <br className="hidden md:block" data-id="element-70" />y empezar a{' '}
          <span className="font-handwriting font-normal text-brand-pink text-4xl md:text-7xl" data-id="element-71">
            elegirte de verdad
          </span>
        </h1>

        <p className="text-base md:text-lg text-gray-600 max-w-2xl mx-auto leading-relaxed mb-6 md:mb-8" data-id="element-72">
          Un programa digital para sanar patrones emocionales, recuperar tu
          autoestima, poner límites sin culpa y dejar de postergarte.
        </p>

        {/* Product image — STATIC (LCP element, no animation for fast mobile load) */}
        <div className="w-full max-w-2xl mx-auto mb-6 md:mb-8" data-id="element-73">
          <img src={HERO_IMAGE} alt="Programa Mi Psicóloga Me Dijo y 6 libros digitales de regalo" className="w-full h-auto object-contain drop-shadow-2xl" width={760} height={507}
        // @ts-ignore - fetchpriority is a valid HTML attribute
        fetchpriority="high" decoding="async" sizes="(max-width: 640px) 92vw, 640px" data-id="element-74" />
        </div>

        {/* Mini horizontal bullets */}
        <div className="flex flex-wrap justify-center gap-x-5 gap-y-2 text-[13px] md:text-sm text-gray-700 font-medium mb-7 max-w-3xl mx-auto" data-id="element-75">
          <span className="inline-flex items-center gap-1.5" data-id="element-76">
            <Check className="w-4 h-4 text-green-600" strokeWidth={3} data-id="element-77" />
            Acceso inmediato
          </span>
          <span className="inline-flex items-center gap-1.5" data-id="element-78">
            <Check className="w-4 h-4 text-green-600" strokeWidth={3} data-id="element-79" />
            Programa completo
          </span>
          <span className="inline-flex items-center gap-1.5" data-id="element-80">
            <Check className="w-4 h-4 text-green-600" strokeWidth={3} data-id="element-81" />
            +6 libros digitales de regalo
          </span>
          <span className="inline-flex items-center gap-1.5" data-id="element-82">
            <Check className="w-4 h-4 text-green-600" strokeWidth={3} data-id="element-83" />
            Celular y PC
          </span>
        </div>

        {/* Price stack */}
        <div className="mb-5" data-id="element-84">
          <p className="text-sm text-gray-400 font-medium line-through" data-id="element-85">
            Precio normal {PRICE_OLD} ARS
          </p>
          <div className="mt-1 flex items-baseline justify-center gap-2" data-id="element-86">
            <span className="text-4xl md:text-6xl font-black text-gray-900 tracking-tight" data-id="element-87">
              {PRICE_NEW}
            </span>
            <span className="text-base md:text-lg font-semibold text-gray-500" data-id="element-88">
              ARS
            </span>
          </div>
          <p className="text-xs md:text-sm text-gray-500 mt-1" data-id="element-89">
            Pago único · sin suscripciones
          </p>
        </div>

        <CTAButton text="QUIERO EMPEZAR A ELEGIRME" data-id="element-90" />

        <div className="flex flex-wrap justify-center gap-x-5 gap-y-2 mt-5 text-[11px] md:text-xs text-gray-500 font-medium" data-id="element-91">
          <span className="inline-flex items-center gap-1.5" data-id="element-92">
            <Lock className="w-3.5 h-3.5 text-green-600" data-id="element-93" /> Compra 100% segura
          </span>
          <span className="inline-flex items-center gap-1.5" data-id="element-94">
            <ShieldCheck className="w-3.5 h-3.5 text-green-600" data-id="element-95" /> Garantía 7
            días
          </span>
          <span className="inline-flex items-center gap-1.5" data-id="element-96">
            <Mail className="w-3.5 h-3.5 text-blue-600" data-id="element-97" /> Acceso inmediato
          </span>
        </div>
      </section>

      {/* ============ PROBLEM (compact) ============ */}
      <Section className="bg-gradient-to-b from-rose-50/60 to-white border-y border-rose-100/60" data-id="element-98">
        <div className="max-w-3xl mx-auto" data-id="element-99">
          <h2 className="text-2xl md:text-4xl font-black text-center text-gray-900 mb-2" data-id="element-100">
            Si sentís que…
          </h2>
          <p className="text-center text-gray-500 text-sm md:text-base mb-8" data-id="element-101">
            quizás te identificás con esto
          </p>

          <div className="grid sm:grid-cols-2 gap-3 max-w-2xl mx-auto mb-8" data-id="element-102">
            {['Estás emocionalmente agotada', 'Te cuesta soltar aunque sabés que deberías', 'Siempre terminás poniéndote última', 'Repetís los mismos patrones una y otra vez', 'Te cuesta poner límites sin sentir culpa'].map((t, i) => <div key={i} className="bg-white border border-gray-100 rounded-xl px-4 py-3 text-[14px] md:text-[15px] font-medium text-gray-700 shadow-sm" data-id="element-103">
                {t}
              </div>)}
          </div>

          <p className="text-lg md:text-2xl font-bold text-center text-gray-900" data-id="element-104">
            Entonces este programa fue{' '}
            <span className="text-brand-pink" data-id="element-105">creado para vos.</span>
          </p>
        </div>
      </Section>

      {/* ============ EMOTIONAL BLACK ============ */}
      <section className="bg-brand-dark text-white px-4 py-10 md:py-16 relative overflow-hidden" data-id="element-106">
        <div className="absolute inset-0 opacity-[0.08] pointer-events-none" data-id="element-107">
          <div className="absolute top-[-20%] left-[-10%] w-[60%] h-[60%] bg-brand-pink rounded-full blur-[140px]" data-id="element-108"></div>
        </div>

        <div className="max-w-3xl mx-auto text-center relative z-10" data-id="element-109">
          <p className="text-pink-300 text-xs md:text-sm font-semibold uppercase tracking-[0.2em] mb-4" data-id="element-110">
            Lo que nadie te dijo
          </p>
          <h2 className="text-3xl md:text-5xl font-black mb-5 leading-tight" data-id="element-111">
            No es que{' '}
            <span className="italic font-handwriting font-normal text-brand-pink" data-id="element-112">
              no puedas.
            </span>
          </h2>
          <p className="text-lg md:text-2xl text-gray-200 font-medium leading-relaxed mb-8" data-id="element-113">
            Es que nadie te enseñó cómo{' '}
            <span className="text-brand-yellow font-bold" data-id="element-114">elegirte</span> sin
            sentir que estás lastimando a los demás.
          </p>

          <p className="text-sm md:text-base text-gray-400 mb-5" data-id="element-115">
            Durante años aprendiste a:
          </p>
          <div className="flex flex-wrap justify-center gap-2 mb-8" data-id="element-116">
            {['aguantar', 'callarte', 'priorizar a todos', 'postergarte'].map(w => <span key={w} className="bg-white/5 border border-white/10 px-4 py-1.5 rounded-full text-sm md:text-base font-medium" data-id="element-117">
                  {w}
                </span>)}
          </div>

          <p className="text-base md:text-xl text-gray-300 font-medium max-w-2xl mx-auto" data-id="element-118">
            Y eso termina destruyendo tu autoestima sin que te des cuenta.
          </p>
        </div>
      </section>

      {/* ============ SOLUTION ============ */}
      <Section className="bg-white" data-id="element-119">
        <div className="max-w-4xl mx-auto text-center" data-id="element-120">
          <p className="text-brand-pink text-xs md:text-sm font-semibold uppercase tracking-[0.2em] mb-3" data-id="element-121">
            La solución
          </p>
          <h2 className="text-2xl md:text-4xl font-black text-gray-900 mb-2" data-id="element-122">
            Mi Psicóloga Me Dijo
          </h2>
          <p className="font-handwriting text-2xl md:text-3xl text-brand-pink mb-6" data-id="element-123">
            Programa Elegirme Primero
          </p>
          <p className="text-base md:text-lg text-gray-600 max-w-2xl mx-auto leading-relaxed mb-10" data-id="element-124">
            Una guía emocional simple, cercana y práctica, diseñada para que
            recuperes tu valor, sanes vínculos que te lastiman y vuelvas a
            conectar con vos misma.
          </p>

          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 max-w-3xl mx-auto" data-id="element-125">
            {['Recuperar tu valor personal', 'Sanar vínculos que te lastiman', 'Dejar de depender emocionalmente', 'Entender tus patrones emocionales', 'Volver a conectar con vos', 'Poner límites sin sentir culpa'].map((t, i) => <div key={i} className="bg-pink-50/60 border border-pink-100 rounded-xl px-3 py-3 text-left text-[13px] md:text-sm font-medium text-gray-800 flex items-start gap-2" data-id="element-126">
                <Check className="w-4 h-4 text-brand-pink flex-shrink-0 mt-0.5" strokeWidth={3} data-id="element-127" />
                <span data-id="element-128">{t}</span>
              </div>)}
          </div>
        </div>
      </Section>

      {/* ============ METHOD / HOW IT WORKS ============ */}
      <Section className="bg-gradient-to-b from-pink-50/40 to-white border-y border-pink-100/40" data-id="element-129">
        <div className="max-w-4xl mx-auto" data-id="element-130">
          <div className="text-center mb-10" data-id="element-131">
            <p className="text-brand-pink text-xs md:text-sm font-semibold uppercase tracking-[0.2em] mb-3" data-id="element-132">
              El método
            </p>
            <h2 className="text-2xl md:text-4xl font-black text-gray-900 mb-3" data-id="element-133">
              Un proceso guiado de transformación
            </h2>
            <p className="text-gray-600 text-base md:text-lg max-w-2xl mx-auto" data-id="element-134">
              No es solo lectura. Es un recorrido por las áreas más importantes
              de tu mundo emocional, paso a paso.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 gap-4 max-w-3xl mx-auto" data-id="element-135">
            {[{
            step: 'Etapa 01',
            title: 'Reconectar con tu autoestima',
            desc: 'Recuperá tu valor personal y dejá de buscar afuera lo que ya está en vos.'
          }, {
            step: 'Etapa 02',
            title: 'Aprender a poner límites',
            desc: 'Decí que no sin culpa, sin justificarte y sin sentir que sos mala persona.'
          }, {
            step: 'Etapa 03',
            title: 'Sanar vínculos que duelen',
            desc: 'Identificá relaciones que te lastiman y aprendé a soltar lo que ya no te suma.'
          }, {
            step: 'Etapa 04',
            title: 'Dejar de postergarte',
            desc: 'Cortá el ciclo de poner a todos primero y empezá a construir una versión más segura de vos.'
          }].map((s, i) => <div key={i} className="bg-white border border-gray-100 rounded-2xl p-5 shadow-sm hover:shadow-md transition-shadow" data-id="element-136">
                <p className="text-[11px] font-bold uppercase tracking-[0.15em] text-brand-pink mb-1" data-id="element-137">
                  {s.step}
                </p>
                <h3 className="text-lg md:text-xl font-bold text-gray-900 mb-1.5" data-id="element-138">
                  {s.title}
                </h3>
                <p className="text-sm md:text-[15px] text-gray-600 leading-relaxed" data-id="element-139">
                  {s.desc}
                </p>
              </div>)}
          </div>
        </div>
      </Section>

      {/* ============ BENEFITS (compact) ============ */}
      <Section className="bg-white" data-id="element-140">
        <div className="max-w-3xl mx-auto" data-id="element-141">
          <div className="text-center mb-8" data-id="element-142">
            <p className="text-brand-pink text-xs md:text-sm font-semibold uppercase tracking-[0.2em] mb-3" data-id="element-143">
              Lo que vas a lograr
            </p>
            <h2 className="text-2xl md:text-4xl font-black text-gray-900" data-id="element-144">
              ¿Qué va a cambiar en tu vida?
            </h2>
          </div>

          <div className="space-y-3 bg-gray-50/70 border border-gray-100 rounded-2xl p-5 md:p-8 mb-8" data-id="element-145">
            <CheckRow data-id="element-146">
              Vas a dejar de cargar con problemas que no son tuyos.
            </CheckRow>
            <CheckRow data-id="element-147">Aprenderás a soltar sin sentir culpa.</CheckRow>
            <CheckRow data-id="element-148">Dejarás de postergarte constantemente.</CheckRow>
            <CheckRow data-id="element-149">
              Vas a entender por qué repetís las mismas historias.
            </CheckRow>
            <CheckRow data-id="element-150">
              Construirás una versión de vos con más paz y autoestima.
            </CheckRow>
          </div>

          <div className="text-center" data-id="element-151">
            <CTAButton text="SÍ, QUIERO TRANSFORMAR MI VIDA HOY" data-id="element-152" />
            <p className="text-xs text-gray-500 mt-3" data-id="element-153">
              Acceso inmediato · Garantía de 7 días
            </p>
          </div>
        </div>
      </Section>

      {/* ============ BONUSES (moved up, denser) ============ */}
      <Section className="bg-gradient-to-b from-pink-50/70 to-white border-y border-pink-100" data-id="element-154">
        <div className="max-w-4xl mx-auto" data-id="element-155">
          <div className="text-center mb-8" data-id="element-156">
            <div className="inline-flex items-center gap-1.5 bg-brand-pink/10 text-brand-pink text-[11px] md:text-xs font-bold px-3 py-1.5 rounded-full mb-3 uppercase tracking-wider" data-id="element-157">
              <Sparkles className="w-3 h-3" data-id="element-158" /> Solo por hoy
            </div>
            <h2 className="text-2xl md:text-4xl font-black text-gray-900 mb-2" data-id="element-159">
              Llevate{' '}
              <span className="text-brand-pink" data-id="element-160">6 libros digitales</span> de
              regalo
            </h2>
            <p className="text-gray-600 text-sm md:text-base" data-id="element-161">
              Acompañan tu proceso de transformación emocional. Hoy{' '}
              <span className="font-bold text-gray-900" data-id="element-162">100% gratis</span>{' '}
              incluidos con tu programa.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-3xl mx-auto mb-6" data-id="element-163">
            <BonusItem img="https://cdn.magicpatterns.com/uploads/wiGS2FmPw2CDsoeUGUQ62W/bono_hoy_te_dejo_ir_checkout.webp" title="Hoy Te Dejo Ir" value="$14.990" data-id="element-164" />
            <BonusItem img="https://cdn.magicpatterns.com/uploads/h18Qrb3RQaJA5EdsKxMAW6/bono_soltar_para_sanar_checkout.webp" title="Soltar para Sanar" value="$14.990" data-id="element-165" />
            <BonusItem img="https://cdn.magicpatterns.com/uploads/q3QbUF49awEHqdjqrRovq3/tu_eres_tu_prioridad_checkout_ultralight.webp" title="Tú Eres Tu Prioridad" value="$14.990" data-id="element-166" />
            <BonusItem img="https://cdn.magicpatterns.com/uploads/bfW4NZrCzv1nLJ3Xq8md6u/vuelvo_a_mi_checkout_ultralight.webp" title="Vuelvo a Mí" value="$14.990" data-id="element-167" />
            <BonusItem img="https://cdn.magicpatterns.com/uploads/pJWaf5buRc7gmAbTszUstm/si_lo_crees_checkout_compressed.webp" title="Si Lo Crees, Lo Creas" value="$14.990" data-id="element-168" />
            <BonusItem img="https://cdn.magicpatterns.com/uploads/e2LxqthMoBAG9ika7rzg4F/merezco_mucho_mas_checkout_compressed.webp" title="Merezco Mucho Más" value="$14.990" data-id="element-169" />
          </div>

          <p className="text-center text-xs md:text-sm text-gray-500" data-id="element-170">
            Todos los libros se entregan en formato PDF · acceso de por vida
          </p>
        </div>
      </Section>

      {/* ============ VALUE STACK ============ */}
      <Section className="bg-white" data-id="element-171">
        <div className="max-w-2xl mx-auto" data-id="element-172">
          <div className="text-center mb-6" data-id="element-173">
            <p className="text-brand-pink text-xs md:text-sm font-semibold uppercase tracking-[0.2em] mb-3" data-id="element-174">
              Todo lo que recibís hoy
            </p>
            <h2 className="text-2xl md:text-3xl font-black text-gray-900" data-id="element-175">
              El combo completo
            </h2>
          </div>

          <div className="bg-white border border-gray-200 rounded-2xl p-5 md:p-7 shadow-sm divide-y divide-gray-100" data-id="element-176">
            {[{
            t: 'Programa "Mi Psicóloga Me Dijo" – 110 sesiones',
            v: '$49.990'
          }, {
            t: 'Libro · Hoy Te Dejo Ir',
            v: '$14.990'
          }, {
            t: 'Libro · Soltar para Sanar',
            v: '$14.990'
          }, {
            t: 'Libro · Tú Eres Tu Prioridad',
            v: '$14.990'
          }, {
            t: 'Libro · Vuelvo a Mí',
            v: '$14.990'
          }, {
            t: 'Libro · Si Lo Crees, Lo Creas',
            v: '$14.990'
          }, {
            t: 'Libro · Merezco Mucho Más',
            v: '$14.990'
          }].map((row, i) => <div key={i} className="flex items-center justify-between py-2.5 text-sm md:text-[15px]" data-id="element-177">
                <span className="text-gray-700 pr-3" data-id="element-178">{row.t}</span>
                <span className="text-gray-400 line-through font-medium flex-shrink-0" data-id="element-179">
                  {row.v}
                </span>
              </div>)}

            <div className="pt-4 mt-1" data-id="element-180">
              <div className="flex items-center justify-between text-sm md:text-base" data-id="element-181">
                <span className="text-gray-500 font-medium" data-id="element-182">
                  Valor total real
                </span>
                <span className="text-gray-400 line-through font-bold" data-id="element-183">
                  $139.930
                </span>
              </div>
              <div className="flex items-center justify-between mt-2" data-id="element-184">
                <span className="text-base md:text-lg font-bold text-gray-900" data-id="element-185">
                  Hoy pagás solo
                </span>
                <span className="text-2xl md:text-3xl font-black text-brand-pink" data-id="element-186">
                  {PRICE_NEW} ARS
                </span>
              </div>
            </div>
          </div>

          <div className="mt-6 text-center" data-id="element-187">
            <CTAButton text="QUIERO EL COMBO COMPLETO" data-id="element-188" />
            <p className="text-xs text-gray-500 mt-3 inline-flex items-center gap-1.5" data-id="element-189">
              <ShieldCheck className="w-3.5 h-3.5 text-green-600" data-id="element-190" />
              Garantía 7 días o te devolvemos el 100% de tu dinero
            </p>
          </div>
        </div>
      </Section>

      {/* ============ TESTIMONIALS (compact dense) ============ */}
      <Section className="bg-gradient-to-b from-rose-50/50 to-white border-y border-rose-100/50" data-id="element-191">
        <div className="max-w-6xl mx-auto" data-id="element-192">
          <div className="text-center mb-8" data-id="element-193">
            <div className="inline-flex items-center gap-1.5 bg-white border border-pink-100 text-brand-pink text-[11px] md:text-xs font-semibold px-3 py-1.5 rounded-full mb-3 uppercase tracking-wider" data-id="element-194">
              <Heart className="w-3 h-3" fill="currentColor" data-id="element-195" /> Miles ya
              empezaron
            </div>
            <h2 className="text-2xl md:text-4xl font-black text-gray-900 mb-2" data-id="element-196">
              Historias reales de transformación
            </h2>
            <div className="inline-flex items-center gap-1 mt-2" data-id="element-197">
              {[...Array(5)].map((_, i) => <Star key={i} className="w-4 h-4 fill-yellow-400 text-yellow-400" data-id="element-198" />)}
              <span className="text-sm font-semibold text-gray-700 ml-1.5" data-id="element-199">
                4.9 / 5
              </span>
              <span className="text-xs text-gray-500 ml-1" data-id="element-200">
                · +2.000 mujeres
              </span>
            </div>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 md:gap-4 max-w-5xl mx-auto" data-id="element-201">
            <Testimonial quote="Por primera vez pude decir que no sin sentir culpa." name="Cristina" country="España" data-id="element-202" />
            <Testimonial quote="Sentí que alguien finalmente entendía lo que me pasaba." name="Sofía" country="México" data-id="element-203" />
            <Testimonial quote="Me ayudó a soltar una relación que me hacía mal." name="Paulina" country="Argentina" data-id="element-204" />
            <Testimonial quote="Ahora entiendo por qué repetía siempre lo mismo." name="María" country="Colombia" data-id="element-205" />
            <Testimonial quote="Sentí que recuperé mi autoestima de a poco." name="Valentina" country="Chile" data-id="element-206" />
            <Testimonial quote="Me siento mucho más fuerte emocionalmente." name="Camila" country="Perú" data-id="element-207" />
            <Testimonial quote="Por fin dejé de ponerme última en mi vida." name="Lucía" country="Uruguay" data-id="element-208" />
            <Testimonial quote="Aprendí a poner límites sin sentirme mala persona." name="Andrea" country="Venezuela" data-id="element-209" />
          </div>
        </div>
      </Section>

      {/* ============ IDENTIFICATION (compact) ============ */}
      <Section className="bg-white" data-id="element-210">
        <div className="max-w-3xl mx-auto text-center" data-id="element-211">
          <h2 className="text-xl md:text-3xl font-black text-gray-900 mb-3" data-id="element-212">
            Este programa{' '}
            <span className="text-red-500 underline decoration-2 underline-offset-4" data-id="element-213">
              NO
            </span>{' '}
            es para todas.
          </h2>
          <p className="text-gray-600 mb-7 text-sm md:text-base" data-id="element-214">
            Es para mujeres que están listas para dejar de sufrir en silencio.
          </p>

          <div className="grid sm:grid-cols-2 gap-2.5 max-w-xl mx-auto text-left" data-id="element-215">
            {['Están cansadas de sufrir en silencio', 'Quieren recuperar su autoestima', 'Desean sanar emocionalmente', 'Quieren empezar a elegirse de verdad'].map((t, i) => <div key={i} className="flex items-start gap-2 bg-gray-50/70 border border-gray-100 rounded-xl px-3.5 py-3 text-[14px] font-medium text-gray-700" data-id="element-216">
                <Check className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" strokeWidth={3} data-id="element-217" />
                <span data-id="element-218">{t}</span>
              </div>)}
          </div>
        </div>
      </Section>

      {/* ============ FINAL OFFER (BLACK) ============ */}
      <section className="bg-brand-dark text-white py-10 md:py-16 px-4 relative overflow-hidden" data-id="element-219">
        <div className="absolute inset-0 opacity-[0.08] pointer-events-none" data-id="element-220">
          <div className="absolute top-[10%] right-[-10%] w-[50%] h-[50%] bg-brand-pink rounded-full blur-[140px]" data-id="element-221"></div>
        </div>

        <div className="max-w-2xl mx-auto relative z-10" data-id="element-222">
          <div className="text-center mb-8" data-id="element-223">
            <div className="inline-flex items-center gap-1.5 bg-white/10 border border-white/15 text-white text-[11px] md:text-xs font-semibold px-3 py-1.5 rounded-full mb-4 uppercase tracking-wider" data-id="element-224">
              <Clock className="w-3 h-3" data-id="element-225" />
              Oferta por tiempo limitado
            </div>
            <h2 className="text-3xl md:text-5xl font-black mb-3" data-id="element-226">
              Empezá hoy tu transformación
            </h2>
            <p className="text-gray-300 text-base md:text-lg" data-id="element-227">
              Acceso inmediato al programa completo + 6 libros de regalo
            </p>
          </div>

          <div className="bg-white text-gray-900 rounded-3xl shadow-2xl p-6 md:p-8" data-id="element-228">
            <div className="text-center pb-6 border-b border-gray-100" data-id="element-229">
              <p className="text-sm text-gray-400 line-through font-medium mb-1" data-id="element-230">
                Antes {PRICE_OLD} ARS
              </p>
              <div className="flex items-baseline justify-center gap-1.5" data-id="element-231">
                <span className="text-5xl md:text-6xl font-black text-gray-900 tracking-tight" data-id="element-232">
                  {PRICE_NEW}
                </span>
                <span className="text-lg font-semibold text-gray-500" data-id="element-233">ARS</span>
              </div>
              <p className="text-xs md:text-sm text-gray-500 mt-1" data-id="element-234">
                Pago único · acceso de por vida
              </p>
            </div>

            <div className="py-5 space-y-2.5" data-id="element-235">
              <CheckRow data-id="element-236">Programa completo "Mi Psicóloga Me Dijo"</CheckRow>
              <CheckRow data-id="element-237">6 libros digitales de regalo</CheckRow>
              <CheckRow data-id="element-238">Acceso inmediato en formato PDF</CheckRow>
              <CheckRow data-id="element-239">Compatible con celular y computadora</CheckRow>
              <CheckRow data-id="element-240">Garantía de devolución de 7 días</CheckRow>
            </div>

            <CTAButton text="QUIERO EMPEZAR MI CAMBIO HOY" data-id="element-241" />

            <p className="text-center text-xs md:text-sm text-gray-500 mt-4 leading-relaxed" data-id="element-242">
              Compra 100% segura · Descarga inmediata después del pago.
              <br data-id="element-243" />
              Si no es para vos, te devolvemos el 100% en 7 días.
            </p>
          </div>

          {/* Closing trust badges */}
          <div className="grid grid-cols-3 gap-3 mt-8 text-center text-gray-300" data-id="element-244">
            <div className="flex flex-col items-center gap-1.5" data-id="element-245">
              <Lock className="w-5 h-5 text-green-400" data-id="element-246" />
              <span className="text-[11px] md:text-xs font-medium" data-id="element-247">
                Pago seguro
              </span>
            </div>
            <div className="flex flex-col items-center gap-1.5" data-id="element-248">
              <Mail className="w-5 h-5 text-blue-400" data-id="element-249" />
              <span className="text-[11px] md:text-xs font-medium" data-id="element-250">
                Acceso inmediato
              </span>
            </div>
            <div className="flex flex-col items-center gap-1.5" data-id="element-251">
              <Smartphone className="w-5 h-5 text-pink-400" data-id="element-252" />
              <span className="text-[11px] md:text-xs font-medium" data-id="element-253">
                Cualquier dispositivo
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* Footer micro */}
      <footer className="bg-brand-dark text-gray-500 text-[11px] text-center py-4 border-t border-white/5 px-4" data-id="element-254">
        © Mi Psicóloga Me Dijo · Todos los derechos reservados
      </footer>

      <StickyCTA data-id="element-255" />
      <UpsellModal data-id="element-256" />
    </div>;
}